// @i18n-ignore-file
// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable i18next/no-literal-string */
import { API_QUINCE_CUSTOMER_ENDPOINT } from '@src/config/env'
import { getLoginCallbackUrl } from '@src/modules/auth/login-callback-uri'
import { getLocalisationHeaders } from '@src/utils/general.utils'

import type { UniversalAuthConfig } from '@lastbrand/fe-common/universal-auth'

const IDENTITY_CONFIG_PATH = 'identity-config'

function getIdentityConfigUrl(): string {
  const base = API_QUINCE_CUSTOMER_ENDPOINT.replace(/\/?$/, '')
  return `${base}/${IDENTITY_CONFIG_PATH}`
}

/** Response from Customer Service identity-config API (region or region + email). */
export interface IdentityConfigApiResponse {
  cognitoIdentityPoolId: string
  cognitoDomain: string
  cognitoRegion: string
  cognitoPoolId: string
  cognitoClientId: string
  /** Present when email was sent: true = new user (show Sign Up), false = existing user (show Sign In). */
  isNewUser?: boolean
}

/** Request body for identity-config by region only (app init). */
export interface IdentityConfigByRegionRequest {
  regionId: number
}

/** Request body for identity-config by region + email (user lookup). */
export interface IdentityConfigByEmailRequest extends IdentityConfigByRegionRequest {
  email: string
}

/**
 * Fetches Cognito identity config for the given region (app init).
 * Used to initialize the correct pool before any user interaction (e.g. for Google OAuth).
 */
export async function fetchIdentityConfigByRegion(regionId: number): Promise<IdentityConfigApiResponse> {
  const url = getIdentityConfigUrl()
  const localisationHeaders = getLocalisationHeaders()
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...localisationHeaders,
    },
    body: JSON.stringify({ regionId } as IdentityConfigByRegionRequest),
  })
  if (!res.ok) {
    throw new Error(`Identity config by region failed: ${res.status} ${res.statusText}`)
  }
  return res.json() as Promise<IdentityConfigApiResponse>
}

/**
 * Fetches identity config with email for user lookup (Sign In vs Sign Up).
 * When email is provided, API returns isNewUser and the Cognito config for the user's pool.
 */
export async function fetchIdentityConfigByEmail(regionId: number, email: string): Promise<IdentityConfigApiResponse> {
  const url = getIdentityConfigUrl()
  const localisationHeaders = getLocalisationHeaders()
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...localisationHeaders,
    },
    body: JSON.stringify({ regionId, email: email.trim().toLowerCase() } as IdentityConfigByEmailRequest),
  })
  if (!res.ok) {
    throw new Error(`Identity config by email failed: ${res.status} ${res.statusText}`)
  }
  return res.json() as Promise<IdentityConfigApiResponse>
}

/**
 * Maps API response to UniversalAuthConfig. Merges in oauth and ssr from app config
 * since the API does not return them.
 */
export function mapIdentityConfigToAuthConfig(
  api: IdentityConfigApiResponse,
  options?: { origin?: string },
): UniversalAuthConfig {
  const origin = options?.origin ?? (typeof location === 'undefined' ? '' : location.origin)
  return {
    userPoolId: api.cognitoPoolId,
    userPoolClientId: api.cognitoClientId,
    region: api.cognitoRegion,
    identityPoolId: api.cognitoIdentityPoolId,
    authFlowType: 'USER_PASSWORD_AUTH',
    oauth: {
      domain: api.cognitoDomain,
      scope: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
      redirectSignIn: [getLoginCallbackUrl(origin)],
      redirectSignOut: [origin],
      responseType: 'code',
      providers: ['Google'],
    },
    ssr: true,
  }
}
